<template>
  <zg-form-element
    :no-margin="noMargin"
    :full-width="fullWidth"
    auto-width
  >
    <div
      :class="['zg-checkbox', {
        'zg-checkbox--has-value': shouldBeChecked,
        'zg-checkbox--is-valid': isValid,
        'zg-checkbox--is-invalid': invalid,
        'zg-checkbox--is-focused': focused,
        'zg-checkbox--is-disabled': disabled,
        'zg-checkbox--is-read-only': readOnly,
        'zg-checkbox--has-tooltip': tooltip && !disabled,
        'zg-checkbox--has-tooltip-trigger': tooltip && !disabled
      }]"
    >
      <label :for="name">
        <input
          :id="name"
          :ref="name"
          type="checkbox"
          :name="name"
          :disabled="disabled || readOnly"
          :checked="shouldBeChecked"
          :value="value"
          @click="onClick"
          @focus="onFocus"
          @blur="onBlur"
          @change="onInput"
        >

        <span class="box">
          <zg-transition-fade>
            <zg-icon
              v-if="shouldBeChecked"
              icon="check"
              size="small"
            />
          </zg-transition-fade>
        </span>

        <span
          :class="['label', {
            'label--small': smallLabel
          }]"
        >
          <slot />
        </span>

        <span
          v-if="showRequired"
          class="is-required"
        >
          *
        </span>
      </label>

      <zg-tooltip
        v-if="tooltip && !disabled"
        :title="tooltip.title"
        :text="tooltip.text"
        :visible="focused"
        :hide-trigger="!tooltip.trigger"
        input
      />
    </div>

    <zg-form-error
      :visible="error && invalid"
      type="error"
    >
      {{ error }}
    </zg-form-error>
  </zg-form-element>
</template>

<script>
  import ZgFormElement from '../ZgFormElement/ZgFormElement'
  import ZgFormError from '../ZgFormError/ZgFormError.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgTooltip from '../ZgTooltip/ZgTooltip.vue'
  import ZgTransitionFade from '../ZgTransitionFade/ZgTransitionFade'

  export default {
    name: 'ZgCheckbox',
    components: {
      ZgFormElement,
      ZgTransitionFade,
      ZgIcon,
      ZgTooltip,
      ZgFormError
    },
    model: {
      prop: 'modelValue',
      event: 'change'
    },
    props: {
      name: {
        type: String,
        required: true
      },
      modelValue: {
        type: [Boolean, Array],
        required: false,
        default: false
      },
      valid: {
        type: Boolean,
        required: false,
        default: false
      },
      invalid: {
        type: Boolean,
        required: false,
        default: false
      },
      error: {
        type: String,
        required: false,
        default: null
      },
      value: {
        type: String,
        required: false,
        default: 'on'
      },
      tooltip: {
        type: Object,
        required: false,
        default: null
      },
      showRequired: {
        type: Boolean,
        required: false,
        default: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      readOnly: {
        type: Boolean,
        required: false,
        default: false
      },
      noMargin: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      },
      smallLabel: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      focused: false,
      checkedValue: null
    }),
    computed: {
      shouldBeChecked () {
        if (this.checkedValue !== null) {
          return this.checkedValue
        } else if (Array.isArray(this.modelValue)) {
          return this.modelValue.includes(this.value)
        } else {
          return this.modelValue
        }
      },
      isValid () {
        return !!(this.valid && !this.invalid && !this.disabled)
      }
    },
    methods: {
      onClick (e) {
        this.focused = false
        this.$emit('click', e)
      },
      onFocus (e) {
        this.focused = true
        this.$emit('focus', e)
      },
      onBlur (e) {
        this.focused = false
        this.$emit('blur', e)
      },
      onInput (e) {
        const isChecked = e.target.checked

        if (Array.isArray(this.modelValue)) {
          const newValue = Array.from(this.modelValue)

          if (isChecked) {
            newValue.push(e.target.value)
          } else {
            newValue.splice(newValue.indexOf(e.target.value), 1)
          }
          this.$emit('change', newValue)
        } else {
          this.checkedValue = isChecked
          this.$emit('change', this.checkedValue)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-checkbox {
    position: relative;
    display: flex;
    height: auto;
    width: fit-content;

    label {
      display: inline-flex;
      cursor: pointer;
    }

    input {
      @include visuallyhidden;
    }

    span.box {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: rem(24px);
      height: rem(24px);
      flex-shrink: 0;
      margin-right: rem(12px);
      border: 2px solid color(gray-500);
      border-radius: rem(6px);
      transition: all 0.3s ease;

      ::v-deep svg {
        width: rem(16px);
        height: rem(16px);
        transition: all 0.3s ease;
      }
    }

    span.label {
      @include type-body-m;

      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin: 0;

      ::v-deep h1,
      ::v-deep h2,
      ::v-deep h3,
      ::v-deep h4,
      ::v-deep h5,
      ::v-deep h6 {
        font-family: $font-text;
        font-weight: font-weight(normal);
        color: color(black);
        margin: 0 0 rhythm(xsmall);
      }

      ::v-deep h1 ~ p,
      ::v-deep h2 ~ p,
      ::v-deep h3 ~ p,
      ::v-deep h4 ~ p,
      ::v-deep h5 ~ p,
      ::v-deep h6 ~ p {
        color: color(gray-500);
      }

      ::v-deep p {
        margin: 0;
      }
    }

    span.label.label--small {
      @include type-body-s;

      margin: 0;
    }

    span.is-required {
      color: color(error-500);
      margin-left: rem(4px);
    }

    ::v-deep .zg-tooltip {
      .content {
        bottom: calc(100% + 16px);

        @include mq(medium) {
          min-width: auto;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-checkbox:hover {
    span.box {
      background-color: color(input-border-hover);
      border-color: color(input-border-hover);
    }
  }

  .zg-checkbox--has-value {
    span.box {
      background-color: color(primary-500);
      border-color: color(primary-500);
    }

    &:hover {
      span.box {
        background-color: color(input-border-hover);
        border-color: color(input-border-hover);
      }
    }

  }

  .zg-checkbox--is-focused {
    span.box {
      border-color: color(input-border-hover);
      box-shadow: box-shadow(focus);
    }

    &:hover {
      span.box {
        background-color: color(input-border-hover);
        border-color: color(input-border-hover);
      }
    }
  }

  .zg-checkbox--is-valid {
    span.box {
      background-color: color(primary-500);
      border-color: color(primary-500);
    }

    &:hover {
      span.box {
        background-color: color(primary-500);
        border-color: color(primary-500);
      }
    }
  }

  .zg-checkbox--is-invalid {
    span.box {
      background-color: color(primary-500);
      border-color: color(input-border-invalid);
    }

    &:hover {
      span.box {
        background-color: color(primary-500);
        border-color: color(input-border-invalid);
      }
    }
  }

  .zg-checkbox--is-disabled {
    opacity: 0.5;

    span.box {
      background-color: color(input-border-disabled);
      border-color: color(input-border-disabled);
    }

    &:hover {
      span.box {
        background-color: color(input-border-disabled);
        border-color: color(input-border-disabled);
      }
    }

    label {
      cursor: not-allowed;
    }
  }

  .zg-checkbox--is-read-only {
    span.box {
      background-color: color(input-border-disabled);
      border-color: color(input-border-disabled);
    }

    &:hover {
      span.box {
        background-color: color(input-border-disabled);
        border-color: color(input-border-disabled);
      }
    }

    label {
      cursor: not-allowed;
    }
  }

  // ? ----------------------------------------------------------------------
  // tooltip
  // ? ----------------------------------------------------------------------

  .zg-checkbox--has-tooltip-trigger {
    label {
      padding-right: input(tooltip-width);
    }
  }
</style>
